import React, { useState, useRef } from 'react';
import ImageInfoOverlay from '../../components/ImageInfoOverlay/ImageInfoOverlay';
import ProjectCard from '../../components/ProjectCard/ProjectCard';
import TelescopeIcon from '../../components/icons/TelescopeIcon';
import { getSectionData } from '../../data/usePortfolioData';
import useLazyBackground from '../../hooks/useLazyBackground';
import useScrollProgress from '../../hooks/useScrollProgress';

const Projects = () => {
  const [showOverlay, setShowOverlay] = useState(false);
  const sectionRef = useRef(null);
  const handleInfoClick = () => setShowOverlay(true);
  const handleClose = () => setShowOverlay(false);

  // Get projects data from centralized JSON
  const projectsData = getSectionData('projects');

  const scrollY = useScrollProgress(sectionRef);

  const bgLoaded = useLazyBackground(sectionRef, projectsData.backgroundImage);

  const sectionStyle = {
    ...(bgLoaded ? { backgroundImage: `url('${projectsData.backgroundImage}')` } : {}),
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  // Get projects from centralized data
  const projects = projectsData.projects || [];

  return (
    <section 
      ref={sectionRef}
      id="projects" 
      className="relative min-h-screen flex flex-col justify-center items-center text-white py-16 sm:py-20 px-4 sm:px-6 lg:px-12 w-full"
      style={sectionStyle}
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/45 z-0"></div>
      
      {/* Info Button */}
      <button 
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 p-2 sm:p-3 bg-gradient-to-r from-space-blue-500/20 to-cosmic-purple-500/20 backdrop-blur-sm rounded-full hover:from-space-blue-500/40 hover:to-cosmic-purple-500/40 transition-all duration-300 border border-white/20 hover:border-white/40 group" 
        onClick={handleInfoClick}
        aria-label="Learn more about this background image"
        style={{
          transform: `translateY(${scrollY * 0.3}px)` // Subtle movement for button
        }}
      >
        <TelescopeIcon className="w-5 h-5 sm:w-6 sm:h-6 group-hover:scale-110 transition-transform duration-300" />
      </button>
      
      {/* Content Container */}
      <div 
        className="relative z-10 max-w-7xl mx-auto w-full"
        style={{
          transform: `translateY(${scrollY * 0.5}px)` // Content moves faster than background
        }}
      >
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="font-display text-display-xl mx-auto mb-4 sm:mb-6 text-space-blue-100">
            {projectsData.title}
          </h2>
          <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-space-blue-400 to-cosmic-purple-400 mx-auto mb-6 sm:mb-8 rounded-full"></div>
        </div>
        
        {/* Projects Cards Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8 xl:gap-10 auto-rows-fr">
          {projects.map((project, index) => (
            <div 
              key={index}
              className="animate-fade-in-up h-full"
              style={{
                animationDelay: `${index * 0.2}s`,
                animationFillMode: 'both'
              }}
            >
              <ProjectCard 
                name={project.name}
                description={project.description}
                features={project.features}
                techStack={project.techStack}
                githubUrl={project.githubUrl}
                deploymentUrl={project.deploymentUrl}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Overlay Component */}
      {showOverlay && (
        <ImageInfoOverlay
          title={projectsData.overlayTitle}
          description={projectsData.overlayDescription}
          onClose={handleClose}
        />
      )}
    </section>
  );
};

export default Projects;